import { useState } from "react";
import Auth from "../../Auth";

export const aiTests = [
  {
    name: "Rewrite",
    path: "/api/ai/rewrite",
    stream: true,
    body: {
      prompt: "The quick brown fox jumps over the lazy dog.",
      modifier: "More intense",
    },
  },
  {
    name: "Expand",
    path: "/api/ai/expand",
    stream: true,
    body: {
      summary: "The quick brown fox jumps over the lazy dog.",
      passage: "The quick brown fox jumps over the lazy dog.",
    },
  },
  {
    name: "Brainstorm",
    path: "/api/ai/brainstorm",
    stream: false,
    body: {
      prompt: "The quick brown fox jumps over the lazy dog.",
      n: 5,
      items: ["Foxes running at high speed", "Dogs sleeping in the sun"],
      user_starters: ["Forest animals in motion", "Domestic animals resting"],
      user_rejected: ["Cat related content", "Bird related content"],
      context: "This is for a children's book showing contrast between activity and rest",
    },
  },
  {
    name: "Brainstorm Follow-up",
    path: "/api/ai/brainstorm",
    stream: false,
    body: {
      prompt: "The quick brown fox jumps over the lazy dog.",
      n: 3,
      items: ["Foxes running at high speed", "Dogs sleeping in the sun", "A rabbit hopping through meadows", "A turtle slowly crossing a path"],
      user_starters: ["Forest animals in motion", "Domestic animals resting"],
      user_rejected: ["Cat related content", "Bird related content", "Fish swimming in streams", "Insects buzzing around flowers"],
      context: "This is for a children's book showing contrast between activity and rest",
    },
    description: "Tests follow-up requests with additional items and rejections",
  },
  {
    name: "Brainstorm Sequence - Initial",
    path: "/api/ai/brainstorm",
    stream: false,
    body: {
      prompt: "Things a young cop in Atlanta would say when venting to a friend",
      n: 5,
      context: "The character has a cynical view of police work",
    },
    description: "First request in a sequence of Brainstorm calls",
  },
  {
    name: "Brainstorm Sequence - Follow-up",
    path: "/api/ai/brainstorm",
    stream: false,
    body: {
      prompt: "Things a young cop in Atlanta would say when venting to a friend",
      n: 3,
      items: ["I didn't sign up for all this paperwork", "The sergeant's breathing down my neck again"],
      user_rejected: ["The pay isn't worth the stress", "These night shifts are killing me"],
      context: "The character has a cynical view of police work",
    },
    description: "Follow-up request with kept and rejected items",
  },
  {
    name: "Brainstorm Sequence - Refresh",
    path: "/api/ai/brainstorm",
    stream: false,
    body: {
      prompt: "Things a young cop in Atlanta would say when venting to a friend",
      n: 3,
      items: ["I didn't sign up for all this paperwork", "The sergeant's breathing down my neck again", "Every day it's the same old story"],
      user_rejected: ["The pay isn't worth the stress", "These night shifts are killing me", "I should've been a firefighter instead"],
      refresh: true,
      context: "The character has a cynical view of police work",
    },
    description: "Refresh request with accumulated items",
  },
  {
    name: "Generate Title",
    path: "/api/ai/documentTitle",
    stream: false,
    body: {
      text: `The witch was tired of moving. She had been seeking a place
      to remain undisturbed for a long while, and now at last her search
      was over. She had settled into a small village nestled in a valley
      deep in the woods, far away from otheer people. The village was small
      and humble, but it had everything she needed.
      `,
    },
  },
  {
    name: "Canvas Card",
    path: "/api/ai/canvasCard",
    stream: false,
    body: {
      prompt: `the quick brown fox jumps over the lazy dog`,
    },
  },
  {
    name: "Canvas Outline",
    path: "/api/ai/canvasOutline",
    stream: false,
    body: {
      type: "cat",
      concept: "The quick brown fox jumps over the lazy dog.",
    },
  },
];

export const TEST_STATUS = {
  notRun: "notRun",
  running: "running",
  success: "success",
  error: "error",
};

// Stream callbacks sometimes pass plain objects/strings instead of Error
// instances. Preserve whatever diagnostic fields we get while still rejecting
// with an Error so the admin test runner displays a normal failure.
const normalizeStreamTestError = (error) => {
  if (error instanceof Error) {
    return error;
  }

  const message = typeof error === "string" ? error : error?.message || "Stream test failed";
  const normalizedError = new Error(message);

  if (error?.name) {
    normalizedError.name = error.name;
  }
  if (error?.stack) {
    normalizedError.stack = error.stack;
  }
  if (error && typeof error === "object") {
    normalizedError.originalError = error;
  }

  return normalizedError;
};

const runStreamTest = async (test) => {
  return new Promise(async (resolve, reject) => {
    try {
      let completed = false;

      setTimeout(() => {
        if (!completed) {
          completed = true;
          reject(new Error("Test timed out"));
        }
      }, 45000);

      const eventCallback = () => {
        if (!completed) {
          completed = true;
          resolve(true);
        }
      };

      Auth.streamWithIdToken(
        test.path,
        test.body,
        null,
        eventCallback,
        () => {
          if (!completed) {
            completed = true;
            resolve(true);
          }
        },
        (error) => {
          if (!completed) {
            completed = true;
            reject(normalizeStreamTestError(error));
          }
        }
      );
    } catch (error) {
      reject(error);
    }
  });
};

export const useIntegrationTests = () => {
  const [testStatus, setTestStatus] = useState({});
  const [testResults, setTestResults] = useState({});

  const runTest = async (test) => {
    setTestStatus((prev) => ({
      ...prev,
      [test.name]: TEST_STATUS.running,
    }));
    try {
      if (test.stream) {
        const result = await runStreamTest(test);
        console.log("TEST RESULT", test.name, result);
        setTestResults((prev) => ({
          ...prev,
          [test.name]: { success: true },
        }));
      } else {
        const result = await Auth.fetchWithIdToken(test.path, test.body);
        console.log("TEST RESULT", test.name, result);
        setTestResults((prev) => ({
          ...prev,
          [test.name]: { success: true, data: result },
        }));
      }
      setTestStatus((prev) => ({
        ...prev,
        [test.name]: TEST_STATUS.success,
      }));
    } catch (error) {
      console.error(error);
      setTestResults((prev) => ({
        ...prev,
        [test.name]: { success: false, error: error.message },
      }));
      setTestStatus((prev) => ({
        ...prev,
        [test.name]: TEST_STATUS.error,
      }));
    }
  };
  const tests = aiTests.map((test) => {
    return {
      run: () => runTest(test),
      ...test,
      status: testStatus[test.name] || TEST_STATUS.notRun,
      result: testResults[test.name] || null,
    };
  });
  return {
    tests,
    anyRunning: Object.values(testStatus).some((status) => status === TEST_STATUS.running),
  };
};
